import { useState } from "react";
import styled from "styled-components";
import { Link } from "../Link";
import { Box } from "../Box";

const TabsContainer = styled(Box)`
  ${({ theme: { fontFamily } }) => `
    font-family: ${fontFamily};
    font-weight: bold;
    width: 100%;
  `}
`;

type TabVariant = "underline" | "segmented";

const Nav = styled.div<{ customMarginLeft?: string; variant: TabVariant }>`
  ${({ theme: { colors }, customMarginLeft, variant }) =>
    variant === "segmented"
      ? `
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background: ${colors.gray[10]};
    border: 1px solid ${colors.gray[100]};
    border-radius: 10px;
    margin-left: ${customMarginLeft || "0px"};
  `
      : `
    display: flex;
    border-bottom: 1px solid ${colors.gray[10]};
    align-items: center;
    justify-content: flex-start;
    margin-left: ${
      customMarginLeft || "0px"
    }; // Apply custom margin or default to 0
  `}
`;

const NavLink = styled(Link)<{
  isActive: boolean;
  isDisabled?: boolean;
  variant: TabVariant;
}>`
  ${({ theme: { colors, space, shadows, fontWeights }, isActive, variant }) =>
    variant === "segmented"
      ? `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    box-sizing: border-box;
    cursor: pointer;
    list-style: none;
    border-radius: 7px;
    font-size: 13px;
    font-weight: ${isActive ? fontWeights.semibold : fontWeights.medium};
    color: ${isActive ? colors.darkBlue : colors.gray[50]};
    background: ${isActive ? colors.white : "transparent"};
    box-shadow: ${isActive ? shadows.sm : "none"};
    transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
    white-space: nowrap;

    &:hover {
      color: ${isActive ? colors.darkBlue : colors.gray[60]};
    }

    &:focus {
      outline: none;
    }
  `
      : `
    padding: 1rem 1.5rem;
    cursor: pointer;
    list-style: none;
    color: ${isActive ? colors.gray[80] : colors.gray[50]};
    margin-right: ${space[1]};
    border-bottom: 4px solid ${isActive ? colors.blue.primary : "transparent"};
    transition: color 0.3s ease, border-bottom 0.3s ease;
    &:first-child {
    padding: 1rem 1.5rem 1rem 0rem!important;
}

    &:hover {
      color: ${colors.gray[70]};
      border-bottom-color: ${!isActive ? colors.gray[20] : colors.blue.primary};
    }

    &:focus {
      outline: none;
    }
  `}

  ${({ isDisabled }) =>
    isDisabled
      ? `
    opacity: 0.4;
    cursor: not-allowed;
  `
      : ""}
`;

const TabContent = styled(Box)<{ isActive: boolean }>`
  ${({ isActive }) => `
    opacity: ${isActive ? 1 : 0};
    transition: opacity 0.3s ease, transform 0.3s ease;
    display: ${isActive ? "block" : "none"};
    width: 100%;
  `}
`;

/**
 * Row holding the segmented tab bar and, optionally, an aligned right-hand slot
 * (e.g. a view toggle). Both children are centred on one line, and the row owns
 * the breathing-room top margin so the tab bar and slot stay the same height and
 * baseline (a margin on the tab bar alone would drop it below the slot).
 */
const TabBar = styled.div<{ navOnly?: boolean }>`
  ${({ theme: { space }, navOnly }) => `
    display: flex;
    align-items: center;
    gap: ${space[2]};
    margin-top: ${navOnly ? 0 : space[1]};
  `}
`;

const CustomTabs = ({
  tabs,
  activeTab: controlledActiveTab,
  onTabChange = (_index: number) => {}, // Accepts a number as a parameter
  activeTabIndex = 0,
  customMarginLeft,
  variant = "underline",
  rightSlot,
  showDividerBelowNav = false,
  navOnly = false,
}: {
  tabs: {
    label: React.ReactNode;
    content: React.ReactNode;
    /** Greys the tab out and ignores clicks on it. */
    isDisabled?: boolean;
    /** Tooltip on the tab, e.g. why it is disabled. */
    title?: string;
  }[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  activeTabIndex?: number;
  customMarginLeft?: string;
  variant?: TabVariant;
  /** Optional element rendered inline, immediately after the tab bar. */
  rightSlot?: React.ReactNode;
  /** Renders a divider line between the tab bar and the tab content. Defaults to false (existing behaviour). */
  showDividerBelowNav?: boolean;
  /**
   * Render just the tab bar, with no content area or top margin: for tabs used
   * as a toggle inside a header row, where the empty content area would add
   * height and knock the bar off the row's centre line.
   */
  navOnly?: boolean;
}) => {
  // Internal state for uncontrolled behavior
  const [internalActiveTab, setInternalActiveTab] = useState(activeTabIndex);

  // Determine whether to use controlled or uncontrolled behavior
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabClick = (index) => {
    if (!isControlled) {
      setInternalActiveTab(index);
    }

    onTabChange(index);
  };

  const nav = (
    <Nav customMarginLeft={customMarginLeft} variant={variant}>
      {tabs.map((tab, index) => (
        <NavLink
          key={index}
          isActive={activeTab === index}
          isDisabled={tab.isDisabled}
          aria-disabled={tab.isDisabled || undefined}
          title={tab.title}
          variant={variant}
          onClick={() => {
            if (!tab.isDisabled) handleTabClick(index);
          }}
        >
          {tab.label}
        </NavLink>
      ))}
    </Nav>
  );

  return (
    <TabsContainer>
      {variant === "segmented" ? (
        <TabBar navOnly={navOnly}>
          {nav}
          {rightSlot}
        </TabBar>
      ) : (
        nav
      )}
      {showDividerBelowNav && (
        <Box borderBottom="1px solid" borderBottomColor="gray.30" />
      )}
      {!navOnly &&
        tabs.map((tab, index) => (
          <TabContent my={3} key={index} isActive={activeTab === index}>
            {tab.content}
          </TabContent>
        ))}
    </TabsContainer>
  );
};

export default CustomTabs;
